<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import {
  EyeIcon,
  ExternalIcon,
  LinkIcon,
  GlobeIcon,
  RefreshIcon,
  // FileExportIcon,
  // IndianRupeeIcon,
} from '@/icons'
// ✅ Import from your enhanced badgeUtils
import {
  getBadgeStatus,
  getWorkflowLabel
} from '@/utils/badgeUtils'
import router from '@/router'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import type { AxiosError } from 'axios'
// import StatsCards from '@/components/cards/StatsCards.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import EnvelopeIcon from '@/icons/EnvelopeIcon.vue'
import MobileIcon from '@/icons/MobileIcon.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

// Define interface for lost tenders with agent info
type LostTenderResponse = Tender & {
  assignedAgent?: string
  assignedAgentName?: string
  assignedAgentEmail?: string
  assignedAgentPhone?: string
  lostAt?: string
  lostReason?: string
  bidAmount?: number
  competitor?: string
  feedback?: string
}

const tenders = ref<LostTenderResponse[]>([])
const searchFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

// Lost tender details modal
const isLostDetailsModalOpen = ref(false)
const selectedLostTender = ref<LostTenderResponse | null>(null)

// Statistics
// const lostCount = computed(() => tenders.value.length)
const totalBidAmount = computed(() =>
  tenders.value.reduce((sum, tender) => sum + (tender.bidAmount || tender.estimatedCost || 0), 0),
)

// Format number for display
const formatNumber = (num: number): string => {
  if (!num) return '0'
  if (num >= 10000000) {
    return (num / 10000000).toFixed(2) + ' Cr'
  } else if (num >= 100000) {
    return (num / 100000).toFixed(2) + ' L'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(2) + ' K'
  }
  return num.toLocaleString('en-IN')
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Format datetime
const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

// Fetch lost tenders from API
const fetchLostTenders = async () => {
  try {
    loading.value = true
    error.value = null

    const response = await api.get('/api/tenders/lost')

    if (response.data.success) {
      tenders.value = response.data.tenders.map((tender: LostTenderResponse) => ({
        ...tender,
        assignedAgentName: tender.assignedAgentName || tender.assignedAgent || 'Unknown Agent',
        lostReason: tender.lostReason || '',
        competitor: tender.competitor || '',
        feedback: tender.feedback || '',
        bidAmount: tender.bidAmount || 0,
        lostAt: tender.lostAt || '',
      }))
      console.log(`✅ Loaded ${tenders.value.length} lost tenders`, tenders.value)
    } else {
      error.value = response.data.error || 'Failed to load lost tenders'
    }
  } catch (err: unknown) {
    console.error('Error fetching lost tenders:', err)
    error.value = getAxiosErrorMessage(err as AxiosError)
  } finally {
    loading.value = false
  }
}

// Filtered tenders based on search
const filteredTenders = computed(() => {
  if (!searchFilter.value) return tenders.value

  const searchTerm = searchFilter.value.toLowerCase()
  return tenders.value.filter(
    (tender) =>
      tender.t247Id?.toLowerCase().includes(searchTerm) ||
      tender.referenceNo?.toLowerCase().includes(searchTerm) ||
      tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
      tender.organization?.toLowerCase().includes(searchTerm) ||
      tender.location?.toLowerCase().includes(searchTerm) ||
      tender.assignedAgentName?.toLowerCase().includes(searchTerm) ||
      tender.lostReason?.toLowerCase().includes(searchTerm) ||
      tender.competitor?.toLowerCase().includes(searchTerm),
  )
})

const viewTenderDetails = (tender: Tender) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'lost-tenders' },
  })
}

// Clear filters
const clearFilters = () => {
  searchFilter.value = ''
}

// Refresh data
const refreshData = async () => {
  await fetchLostTenders()
}

const handleCloseLostDetailsModal = () => {
  isLostDetailsModalOpen.value = false
  selectedLostTender.value = null
}

// Export lost tenders
// const exportLostTenders = async () => {
//   try {
//     // Create CSV content
//     const headers = [
//       'Tender ID',
//       'Reference No',
//       'Description',
//       'Organization',
//       'Location',
//       'Bid Amount',
//       'Estimated Cost',
//       'Lost Date',
//       'Lost Reason',
//       'Competitor',
//       'Assigned Agent',
//       'Agent Email',
//       'Agent Phone',
//       'Feedback',
//     ]

//     const csvRows = [headers]

//     for (const tender of filteredTenders.value) {
//       csvRows.push([
//         tender.t247Id || '',
//         tender.referenceNo || '',
//         tender.tenderBrief || '',
//         tender.organization || '',
//         tender.location || '',
//         tender.bidAmount ? `₹${formatNumber(tender.bidAmount)}` : '',
//         tender.estimatedCost ? `₹${formatNumber(tender.estimatedCost)}` : '',
//         tender.lostAt ? formatDate(tender.lostAt) : '',
//         tender.lostReason || '',
//         tender.competitor || '',
//         tender.assignedAgentName || '',
//         tender.assignedAgentEmail || '',
//         tender.assignedAgentPhone || '',
//         tender.feedback || '',
//       ])
//     }

//     const csvContent = csvRows
//       .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
//       .join('\n')

//     const blob = new Blob([csvContent], { type: 'text/csv' })
//     const url = window.URL.createObjectURL(blob)
//     const link = document.createElement('a')
//     link.href = url
//     link.setAttribute('download', `lost-tenders-${new Date().toISOString().split('T')[0]}.csv`)
//     document.body.appendChild(link)
//     link.click()
//     link.remove()

//     alert('✅ Lost tenders exported successfully!')
//   } catch (err: unknown) {
//     console.error('Error exporting lost tenders:', err)
//     alert(`Failed to export: ${getAxiosErrorMessage(err as AxiosError)}`)
//   }
// }

// Tooltip management for description
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipTarget = ref<HTMLElement>(); // Changed from null to undefined
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('top');

// Tooltip handlers
const showTooltip = (event: MouseEvent, text: string) => {
  const target = event.currentTarget as HTMLElement;
  tooltipTarget.value = target; // This will be HTMLElement, not null
  tooltipText.value = text;
  tooltipPosition.value = 'top';
  tooltipVisible.value = true;
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipTarget.value = undefined; // Changed from null to undefined
  tooltipText.value = '';
};

// Lifecycle
onMounted(() => {
  fetchLostTenders()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Lost Tenders" />

    <!-- Stats Cards -->
    <!-- <div class="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <StatsCards title="Total Lost Tenders" :value="lostCount" :icon="EyeIcon" />
      <StatsCards
        title="Total Bid Amount"
        :value="`₹${formatNumber(totalBidAmount)}`"
        :icon="IndianRupeeIcon"
      />
    </div> -->

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ error }}</p>
          <button
            @click="refreshData"
            class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
          >
            Click here to retry
          </button>
        </div>
      </div>
    </div>

    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Lost Tenders Details"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex items-center justify-between gap-2">
          <!-- Search Input -->
          <TextInputDefault
            v-model="searchFilter"
            placeholder="Search lost tenders..."
            id="searchlosttenders"
            name="searchlosttenders"
            type="text"
            size="xss"
          />

          <!-- Action Buttons -->
          <div class="flex gap-2 ml-auto">
            <!-- Clear Filter Button -->
            <BaseButton
              v-if="searchFilter"
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
            />

            <!-- Export Button -->
            <!-- <BaseButton
              text="Export"
              size="xss"
              variant="outline"
              :icon="FileExportIcon"
              @click="exportLostTenders"
              :disabled="tenders.length === 0"
            /> -->

            <!-- Refresh Button -->
            <BaseButton
              size="xss"
              :icon="RefreshIcon"
              @click="refreshData"
              :loading="loading"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i
            class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
          ></i>
          Loading lost tender data...
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="w-full flex flex-col items-center text-center py-8 gap-3 text-red-500"
        >
          <p>{{ error }}</p>
          <BaseButton text="Retry" size="xss" @click="refreshData" class="w-30" />
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="tenders.length === 0"
          :icon="GlobeIcon"
          title="No lost tenders"
          description="No tenders have been marked as lost yet"
          hint="Mark tenders as lost from the Agent Tenders or Tender Details page"
        />

        <!-- No Results after Filtering -->
        <EmptyState
          v-else-if="filteredTenders.length === 0"
          :icon="GlobeIcon"
          title="No matching lost tenders"
          description="No lost tenders match your search criteria."
          hint="Try adjusting your search filters"
          action-text="Clear Filters"
          @action="clearFilters"
        />

        <!-- Tender Table -->
        <DataTable
          v-else-if="filteredTenders.length > 0"
          :value="filteredTenders"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="450px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          :pt="{
            table: { style: { borderCollapse: 'separate', borderSpacing: '0' } },
            thead: { style: { position: 'sticky', top: '0', zIndex: '20' } },
            tbody: { style: { position: 'relative', zIndex: '10' } },
          }"
        >
          <!-- Tender ID Column -->
          <Column
            sortable
            frozen
            field="referenceNo"
            header="Reference No"
            headerStyle="position: sticky; left: 48px; z-index: 30;"
            bodyStyle="position: sticky; left: 48px; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex flex-col max-w-[100px] text-xs">
                <a
                  :href="data.tenderBriefLink"
                  target="_blank"
                  class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                  v-if="data.tenderBriefLink"
                >
                  {{ data.referenceNo }}
                  <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                </a>
                <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
              </div>
            </template>
          </Column>

          <!-- Lost Date Column -->
          <Column sortable field="lostAt" header="Lost Date">
            <template #body="{ data }">
              <div class="flex flex-col text-xs">
                {{ formatDate(data.lostAt || '') }}
              </div>
            </template>
          </Column>

          <!-- Status Column - Using getWorkflowLabel and getBadgeStatus -->
          <Column sortable field="workflowStatus" header="Status">
            <template #body="{ data }">
              <Badge
                :value="getWorkflowLabel(data.workflowStatus || 'lost')"
                :class="'p-badge-' + getBadgeStatus(data.workflowStatus || 'lost')"
              />
            </template>
          </Column>

          <!-- Type Column -->
          <Column sortable field="type" header="Type">
            <template #body="{ data }">
              <Badge
                :value="data.sheetType"
                :class="'p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())"
              />
            </template>
          </Column>

          <!-- Tender Brief Column -->
          <Column sortable field="tenderBrief" header="Description">
            <template #body="{ data }">
              <div
                class="line-clamp-2 text-xs cursor-help"
                @mouseenter="(event) => showTooltip(event, data.tenderBrief)"
                @mouseleave="hideTooltip"
              >
                {{ data.tenderBrief }}
              </div>
            </template>
          </Column>

          <!-- Organization Column -->
          <Column sortable field="organization" header="Organization">
            <template #body="{ data }">
              <div class="text-xs">{{ data.organization }}</div>
            </template>
          </Column>

          <!-- Location Column -->
          <Column sortable field="location" header="Location">
            <template #body="{ data }">
              <span class="text-xs">{{ data.location }}</span>
            </template>
          </Column>

          <!-- Bid Amount Column -->
          <Column sortable field="bidAmount" header="Bid Amount">
            <template #body="{ data }">
              <div class="text-xs font-medium text-red-600">
                ₹{{ formatNumber(data.bidAmount || data.estimatedCost || 0) }}
              </div>
              <div v-if="data.bidAmount && data.estimatedCost" class="text-xs text-gray-500">
                <span v-if="data.bidAmount < data.estimatedCost" class="text-green-500">
                  ▼
                  {{
                    (((data.estimatedCost - data.bidAmount) / data.estimatedCost) * 100).toFixed(1)
                  }}% below estimate
                </span>
                <span v-else class="text-red-500">
                  ▲
                  {{
                    (((data.bidAmount - data.estimatedCost) / data.estimatedCost) * 100).toFixed(1)
                  }}% above estimate
                </span>
              </div>
            </template>
          </Column>

          <!-- Lost Reason Column -->
          <Column sortable field="lostReason" header="Lost Reason">
            <template #body="{ data }">
              <div class="text-xs">
                {{ data.lostNotes || 'Not specified' }}
              </div>
              <div v-if="data.competitor" class="text-xs text-gray-500">
                Competitor: {{ data.competitor }}
              </div>
            </template>
          </Column>

          <!-- Assigned Agent Column with Contact Info -->
          <Column sortable field="assignedAgent" header="Assigned Agent">
            <template #body="{ data }">
              <div class="flex flex-col space-y-1">
                <!-- Agent Name -->
                <div class="flex items-center justify-between">
                  <span class="text-xs font-medium text-gray-800">
                    {{ data.assignedAgentName || 'Unassigned' }}
                  </span>
                </div>

                <!-- Agent Contact Info -->
                <div
                  v-if="data.assignedAgentEmail || data.assignedAgentPhone"
                  class="space-y-1.5 text-xs"
                >
                  <!-- Email -->
                  <div
                    v-if="data.assignedAgentEmail && data.assignedAgentEmail !== 'Not available'"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <EnvelopeIcon class="w-4 h-4" />
                    <a
                      :href="`mailto:${data.assignedAgentEmail}`"
                      class="truncate hover:text-blue-600 hover:underline transition-colors"
                      :title="`Email: ${data.assignedAgentEmail}`"
                    >
                      {{ data.assignedAgentEmail }}
                    </a>
                  </div>

                  <!-- Phone -->
                  <div
                    v-if="data.assignedAgentPhone && data.assignedAgentPhone !== 'Not available'"
                    class="flex items-center gap-1 text-gray-600 group"
                  >
                    <MobileIcon class="w-4 h-4" />
                    <a
                      :href="`tel:${data.assignedAgentPhone}`"
                      class="truncate hover:text-green-600 hover:underline transition-colors"
                      :title="`Phone: ${data.assignedAgentPhone}`"
                    >
                      {{ data.assignedAgentPhone }}
                    </a>
                  </div>
                </div>
              </div>
            </template>
          </Column>

          <!-- Action Column -->
          <Column
            frozen
            field="action"
            header="Actions"
            headerStyle="width: 150px; position: sticky; right: 0; z-index: 30;"
            bodyStyle="position: sticky; right: 0; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex gap-2">
                <!-- View button -->
                <BaseButton
                  size="xss"
                  :aria-label="`view ${data.t247Id} details`"
                  @click="viewTenderDetails(data)"
                  variant="secondary"
                  :icon="EyeIcon"
                  tooltip="View Tender Details"
                  tooltipPosition="bottom"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} of {{ tenders.length }} lost tenders
            <span v-if="searchFilter">(filtered)</span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span>Total bid value: ₹{{ formatNumber(totalBidAmount) }}</span>
            <span class="ml-4">Last updated: {{ new Date().toLocaleTimeString() }}</span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <!-- Lost Tender Details Modal -->
  <ModalDefault
    v-model="isLostDetailsModalOpen"
    :title="`Lost Tender Details - ${selectedLostTender?.referenceNo || ''}`"
    width="max-w-2xl"
    classBody="my-4"
  >
    <div v-if="selectedLostTender" class="space-y-6">
      <!-- Basic Tender Info -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <h4 class="text-sm font-medium text-gray-500 mb-1">Tender ID</h4>
          <p class="text-sm">{{ selectedLostTender.t247Id }}</p>
        </div>
        <div>
          <h4 class="text-sm font-medium text-gray-500 mb-1">Reference No</h4>
          <p class="text-sm">{{ selectedLostTender.referenceNo }}</p>
        </div>
        <div>
          <h4 class="text-sm font-medium text-gray-500 mb-1">Organization</h4>
          <p class="text-sm">{{ selectedLostTender.organization }}</p>
        </div>
        <div>
          <h4 class="text-sm font-medium text-gray-500 mb-1">Location</h4>
          <p class="text-sm">{{ selectedLostTender.location }}</p>
        </div>
      </div>

      <!-- Lost Details Section -->
      <div class="border-t pt-4">
        <h4 class="text-sm font-medium text-gray-900 mb-3">Lost Information</h4>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <h4 class="text-sm font-medium text-gray-500 mb-1">Lost Date</h4>
            <p class="text-sm">{{ formatDateTime(selectedLostTender.lostAt || '') }}</p>
          </div>
          <div>
            <h4 class="text-sm font-medium text-gray-500 mb-1">Competitor</h4>
            <p class="text-sm">{{ selectedLostTender.competitor || 'Not specified' }}</p>
          </div>
          <div class="col-span-2">
            <h4 class="text-sm font-medium text-gray-500 mb-1">Lost Reason</h4>
            <p class="text-sm bg-red-50 p-3 rounded border border-red-100">
              {{ selectedLostTender.lostReason || 'No reason specified' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Financial Details -->
      <div class="border-t pt-4">
        <h4 class="text-sm font-medium text-gray-900 mb-3">Financial Details</h4>
        <div class="grid grid-cols-3 gap-4">
          <div>
            <h4 class="text-sm font-medium text-gray-500 mb-1">Estimated Cost</h4>
            <p class="text-sm font-medium">
              ₹{{ formatNumber(selectedLostTender.estimatedCost || 0) }}
            </p>
          </div>
          <div>
            <h4 class="text-sm font-medium text-gray-500 mb-1">Our Bid Amount</h4>
            <p class="text-sm font-medium text-red-600">
              ₹{{ formatNumber(selectedLostTender.bidAmount || 0) }}
            </p>
          </div>
          <div>
            <h4 class="text-sm font-medium text-gray-500 mb-1">Difference</h4>
            <p
              v-if="selectedLostTender.bidAmount && selectedLostTender.estimatedCost"
              class="text-sm font-medium"
              :class="
                selectedLostTender.bidAmount < selectedLostTender.estimatedCost
                  ? 'text-green-600'
                  : 'text-red-600'
              "
            >
              {{ selectedLostTender.bidAmount < selectedLostTender.estimatedCost ? '-' : '+' }}
              ₹{{
                formatNumber(
                  Math.abs(selectedLostTender.estimatedCost - selectedLostTender.bidAmount),
                )
              }}
            </p>
            <p v-else class="text-sm text-gray-500">N/A</p>
          </div>
        </div>
      </div>

      <!-- Assigned Agent Details -->
      <div v-if="selectedLostTender.assignedAgentName" class="border-t pt-4">
        <h4 class="text-sm font-medium text-gray-900 mb-3">Assigned Agent</h4>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <h4 class="text-sm font-medium text-gray-500 mb-1">Agent Name</h4>
            <p class="text-sm">{{ selectedLostTender.assignedAgentName }}</p>
          </div>
          <div v-if="selectedLostTender.assignedAgentEmail">
            <h4 class="text-sm font-medium text-gray-500 mb-1">Agent Email</h4>
            <p class="text-sm">{{ selectedLostTender.assignedAgentEmail }}</p>
          </div>
          <div v-if="selectedLostTender.assignedAgentPhone">
            <h4 class="text-sm font-medium text-gray-500 mb-1">Agent Phone</h4>
            <p class="text-sm">{{ selectedLostTender.assignedAgentPhone }}</p>
          </div>
        </div>
      </div>

      <!-- Feedback Section -->
      <div v-if="selectedLostTender.feedback" class="border-t pt-4">
        <h4 class="text-sm font-medium text-gray-900 mb-3">Feedback / Notes</h4>
        <div class="bg-gray-50 p-4 rounded border border-gray-200">
          <p class="text-sm whitespace-pre-line">{{ selectedLostTender.feedback }}</p>
        </div>
      </div>

      <!-- Tender Link -->
      <div v-if="selectedLostTender.tenderBriefLink" class="border-t pt-4">
        <h4 class="text-sm font-medium text-gray-900 mb-3">Tender Link</h4>
        <a
          :href="selectedLostTender.tenderBriefLink"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2"
        >
          <LinkIcon class="w-4 h-4" />
          {{ selectedLostTender.tenderBriefLink }}
        </a>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Close" variant="secondary" @click="handleCloseLostDetailsModal" />
      </div>
    </template>
  </ModalDefault>

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>

<style scoped>
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}

.transition-colors {
  transition: color 0.2s ease;
}
</style>
